import { Vector3, Matrix4, BufferGeometry, Float32BufferAttribute } from 'three';
import { Part, Flange, FlangeId, EdgeKey } from './types';
import { bendAllowance, deg2rad } from './bendMath';

export interface EdgeFrame {
  edgeStart: Vector3; // point on parent edge where local v=0
  v: Vector3;         // along the edge
  u: Vector3;         // outward from parent body (perpendicular to v, in flange plane)
  top: Vector3;       // +z of parent (flange normal)
}

export function getEdgeFrameInLocal(parent: Flange, edge: EdgeKey): EdgeFrame {
  const top = new Vector3(0, 0, 1);
  switch (edge) {
    case '+x':
      return {
        edgeStart: new Vector3(parent.length, 0, 0),
        v: new Vector3(0, 1, 0),
        u: new Vector3(1, 0, 0),
        top,
      };
    case '-x':
      return {
        edgeStart: new Vector3(0, 0, 0),
        v: new Vector3(0, 1, 0),
        u: new Vector3(-1, 0, 0),
        top,
      };
    case '+y':
      return {
        edgeStart: new Vector3(0, parent.width, 0),
        v: new Vector3(1, 0, 0),
        u: new Vector3(0, 1, 0),
        top,
      };
    case '-y':
      return {
        edgeStart: new Vector3(0, 0, 0),
        v: new Vector3(1, 0, 0),
        u: new Vector3(0, -1, 0),
        top,
      };
  }
}

export function edgeSpan(parent: Flange, edge: EdgeKey): number {
  return edge === '+x' || edge === '-x' ? parent.width : parent.length;
}

export interface FlangeTransform {
  matrix: Matrix4;     // local-to-world
  origin: Vector3;
  xAxis: Vector3;
  yAxis: Vector3;
  zAxis: Vector3;
}

function basisToMatrix(origin: Vector3, x: Vector3, y: Vector3, z: Vector3) {
  const m = new Matrix4();
  m.makeBasis(x, y, z);
  m.setPosition(origin);
  return m;
}

// Compute world transforms for every flange, walking the tree from root.
// Convention: each flange occupies [0,L] × [0,W] × [-T/2, T/2] in its local frame.
// Bend axis sits at distance (R + T/2) from parent's midplane on the inside-of-bend side.
export function computeTransforms(part: Part): Record<FlangeId, FlangeTransform> {
  const T = part.thickness;
  const out: Record<FlangeId, FlangeTransform> = {};

  const walk = (id: FlangeId, parentTransform: FlangeTransform | null) => {
    const f = part.flanges[id];
    if (!f) return;

    if (!f.attachment || !parentTransform) {
      const origin = new Vector3();
      const xAxis = new Vector3(1, 0, 0);
      const yAxis = new Vector3(0, 1, 0);
      const zAxis = new Vector3(0, 0, 1);
      out[id] = {
        matrix: basisToMatrix(origin, xAxis, yAxis, zAxis),
        origin, xAxis, yAxis, zAxis,
      };
    } else {
      const parent = part.flanges[f.attachment.parentId];
      const ef = getEdgeFrameInLocal(parent, f.attachment.edge);
      const theta = deg2rad(f.attachment.bendAngle);
      const s = Math.sin(theta);
      const c = Math.cos(theta);
      const sgn = Math.sign(f.attachment.bendAngle) || 1;
      const RT = f.attachment.bendRadius + T / 2;

      // child origin in parent local
      const localOrigin = new Vector3()
        .copy(ef.edgeStart)
        .addScaledVector(ef.v, f.attachment.offset)
        .addScaledVector(ef.u, RT * Math.abs(s))
        .addScaledVector(ef.top, sgn * RT * (1 - c));

      // child basis in parent local: rotation in (u, top) plane by theta (signed)
      const xAxisLocal = new Vector3()
        .addScaledVector(ef.u, c)
        .addScaledVector(ef.top, s);
      const yAxisLocal = ef.v.clone();
      const zAxisLocal = new Vector3()
        .addScaledVector(ef.u, -s)
        .addScaledVector(ef.top, c);

      // transform local-of-parent to world
      const childMatrixLocal = basisToMatrix(localOrigin, xAxisLocal, yAxisLocal, zAxisLocal);
      const worldMatrix = new Matrix4().multiplyMatrices(parentTransform.matrix, childMatrixLocal);

      const origin = new Vector3().setFromMatrixPosition(worldMatrix);
      const xAxis = new Vector3().setFromMatrixColumn(worldMatrix, 0).normalize();
      const yAxis = new Vector3().setFromMatrixColumn(worldMatrix, 1).normalize();
      const zAxis = new Vector3().setFromMatrixColumn(worldMatrix, 2).normalize();

      out[id] = { matrix: worldMatrix, origin, xAxis, yAxis, zAxis };
    }

    for (const cid of f.children) {
      walk(cid, out[id]);
    }
  };

  walk(part.rootId, null);
  return out;
}

// Build a curved arc (extruded sector) for the bend region between a parent and child.
// Returns a BufferGeometry approximating the bend volume.
//
// `vRange` lets callers clip the v-extent of the bend to match the child
// flange's actual edge after miter cuts. Without this, the bend mesh would
// extend the full child.width even though the flange's edge is shorter at the
// mitered corners — leaving a sliver of bend material poking past the miter.
export function buildBendGeometry(
  part: Part,
  parentId: FlangeId,
  childId: FlangeId,
  parentTransform: FlangeTransform,
  vRange?: { lo: number; hi: number },
): BufferGeometry | null {
  const child = part.flanges[childId];
  if (!child.attachment || child.attachment.parentId !== parentId) return null;
  const T = part.thickness;
  const R = child.attachment.bendRadius;
  const angleDeg = child.attachment.bendAngle;
  const absAngle = Math.abs(angleDeg);
  if (absAngle < 0.01) return null;

  const sgn = Math.sign(angleDeg) || 1;
  const parent = part.flanges[parentId];
  const ef = getEdgeFrameInLocal(parent, child.attachment.edge);

  // Bend axis in parent local: edgeStart + offset*v + sgn*(R + T/2)*top
  const axisCenter = new Vector3()
    .copy(ef.edgeStart)
    .addScaledVector(ef.v, child.attachment.offset)
    .addScaledVector(ef.top, sgn * (R + T / 2));

  // ~3° per segment → 30 segments for a 90° bend. Smooth enough to look like
  // a CAD renderer's curved edge under HDRI lighting without breaking the
  // bank on geometry.
  const segments = Math.max(8, Math.ceil(absAngle / 3));
  const vLo = Math.max(0, vRange?.lo ?? 0);
  const vHi = Math.min(child.width, vRange?.hi ?? child.width);
  if (vHi - vLo < 1e-3) return null;
  const innerR = R;
  const outerR = R + T;

  const positions: number[] = [];
  const indices: number[] = [];

  // For each segment, build 4 vertices (innerStart, outerStart, innerEnd, outerEnd) at v=0 and v=Wc
  // Parameterize angle from 0 to angleDeg signed; rotation in (u, top) plane taking +u toward +top by phi.
  const ringAt = (phi: number) => {
    const cs = Math.cos(phi);
    const sn = Math.sin(phi);
    // direction from axis to outer surface: -sgn*top rotated by phi
    // In (u, top) plane: starts at (0, -sgn) for outer (radius outerR), rotates by phi
    // After rot by phi: (u, top) = (0*cs - (-sgn)*sn, 0*sn + (-sgn)*cs) = (sgn*sn, -sgn*cs)
    // outer point: axis + outerR * (sgn*sn*u + (-sgn*cs)*top)
    const outerOffsetU = sgn * sn * outerR;
    const outerOffsetTop = -sgn * cs * outerR;
    const innerOffsetU = sgn * sn * innerR;
    const innerOffsetTop = -sgn * cs * innerR;
    return { outerOffsetU, outerOffsetTop, innerOffsetU, innerOffsetTop };
  };

  const totalAngleRad = deg2rad(angleDeg);

  for (let i = 0; i <= segments; i++) {
    const phi = (totalAngleRad * i) / segments;
    const r = ringAt(phi);
    const inner0 = new Vector3()
      .copy(axisCenter)
      .addScaledVector(ef.u, r.innerOffsetU)
      .addScaledVector(ef.top, r.innerOffsetTop)
      .addScaledVector(ef.v, vLo);
    const inner1 = new Vector3()
      .copy(axisCenter)
      .addScaledVector(ef.u, r.innerOffsetU)
      .addScaledVector(ef.top, r.innerOffsetTop)
      .addScaledVector(ef.v, vHi);
    const outer0 = new Vector3()
      .copy(axisCenter)
      .addScaledVector(ef.u, r.outerOffsetU)
      .addScaledVector(ef.top, r.outerOffsetTop)
      .addScaledVector(ef.v, vLo);
    const outer1 = new Vector3()
      .copy(axisCenter)
      .addScaledVector(ef.u, r.outerOffsetU)
      .addScaledVector(ef.top, r.outerOffsetTop)
      .addScaledVector(ef.v, vHi);
    positions.push(
      inner0.x, inner0.y, inner0.z,
      inner1.x, inner1.y, inner1.z,
      outer0.x, outer0.y, outer0.z,
      outer1.x, outer1.y, outer1.z,
    );
  }

  // Indices: each segment has 4 vertices for current ring + 4 for next ring.
  // Build inner surface, outer surface, and two end caps (v=0 and v=Wc).
  for (let i = 0; i < segments; i++) {
    const a = i * 4;
    const b = (i + 1) * 4;
    // inner surface: a+0, a+1, b+0, b+1
    indices.push(a + 0, b + 0, a + 1, a + 1, b + 0, b + 1);
    // outer surface: a+2, a+3, b+2, b+3 (reverse winding)
    indices.push(a + 2, a + 3, b + 2, a + 3, b + 3, b + 2);
    // end cap v=0: a+0, a+2, b+0, b+2
    indices.push(a + 0, a + 2, b + 0, a + 2, b + 2, b + 0);
    // end cap v=Wc
    indices.push(a + 1, b + 1, a + 3, a + 3, b + 1, b + 3);
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();

  // Apply parent transform to bring into world frame
  geo.applyMatrix4(parentTransform.matrix);
  return geo;
}

// Bend region length in flat pattern (the "BA strip width")
export function bendStripWidth(angleDeg: number, radius: number, thickness: number, k: number) {
  return bendAllowance(angleDeg, radius, thickness, k);
}
